import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  SITE_NAME,
  getApexFooterLogoSrc,
  getApexHeaderLogoSrc,
} from 'alakai-aloha-shared';
import { PublicSiteShell, PublicUiProvider, ItineraryPilotBanner, useItineraryOffer } from 'alakai-aloha-shared/public-ui';
import { trackEvent } from '../../utils/analytics';
import { buildApexShellConfig, buildIslandShellConfig } from '../../lib/platformLinks';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import ApexShell from '../apex/ApexShell';
import AppLink from './AppLink';

const LayoutCtaContext = createContext({
  headerCta: { label: 'Plan My Trip', href: '/interview', onClick: null },
  footerCta: { label: 'Plan Your Trip', href: '/interview', onClick: null },
  setHeaderCta: () => {},
  resetHeaderCta: () => {},
  setFooterCta: () => {},
  resetFooterCta: () => {},
});

const APEX_SHELL_PATH_PREFIXES = [
  '/quiz/choose-island',
];

export const useLayoutCtas = () => useContext(LayoutCtaContext);

function RouterLinkAdapter({ href, ...props }) {
  return <AppLink to={href} {...props} />;
}

const Layout = ({
  children,
  fullWidth = false,
  headerBg = 'transparent',
  footerBg = 'transparent',
  pageClass = 'bg-site-bg',
  useOceanBg = true,
  headerCtaLabel,
  headerCtaHref,
  headerCtaOnClick,
  footerCtaLabel,
  footerCtaHref,
  footerCtaOnClick,
}) => {
  const location = useLocation();
  const { active: pilotActive } = useItineraryOffer();
  const siteContext = useMemo(() => getRuntimeSiteContext(), []);
  const shouldUseApexShell = siteContext.isApexHost || APEX_SHELL_PATH_PREFIXES.some((path) => (
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  ));
  const isBareApexHomepage = shouldUseApexShell && location.pathname === '/';
  const shellConfig = useMemo(
    () => (shouldUseApexShell ? buildApexShellConfig() : buildIslandShellConfig(siteContext.island)),
    [shouldUseApexShell, siteContext.island],
  );
  const useMinimalIslandFooter = !shouldUseApexShell && Boolean(shellConfig.minimalFooter);
  const islandShellClassName = shouldUseApexShell
    ? undefined
    : (useMinimalIslandFooter ? 'island-shell-minimal-footer' : 'island-shell-full-footer');
  const resolvedHeaderCtaLabel =
    headerCtaLabel ?? (pilotActive && shellConfig.showHeaderCta ? 'Plan My Trip Free' : shellConfig.headerCtaLabel) ?? shellConfig.footerPrimaryLinks[1]?.label ?? 'Plan My Trip';
  const resolvedHeaderCtaHref =
    headerCtaHref ?? shellConfig.headerCtaHref ?? shellConfig.footerPrimaryLinks[1]?.to ?? '/interview';
  const resolvedFooterCtaLabel = shouldUseApexShell
    ? footerCtaLabel ?? resolvedHeaderCtaLabel
    : footerCtaLabel ?? shellConfig.footerPrimaryLinks[1]?.label ?? resolvedHeaderCtaLabel;
  const resolvedFooterCtaHref = shouldUseApexShell
    ? footerCtaHref ?? resolvedHeaderCtaHref
    : footerCtaHref ?? shellConfig.footerPrimaryLinks[1]?.to ?? resolvedHeaderCtaHref;
  const defaultHeaderCta = useMemo(
    () => ({ label: resolvedHeaderCtaLabel, href: resolvedHeaderCtaHref, onClick: headerCtaOnClick || null }),
    [headerCtaOnClick, resolvedHeaderCtaHref, resolvedHeaderCtaLabel],
  );
  const defaultFooterCta = useMemo(
    () => ({ label: resolvedFooterCtaLabel, href: resolvedFooterCtaHref, onClick: footerCtaOnClick || null }),
    [footerCtaOnClick, resolvedFooterCtaHref, resolvedFooterCtaLabel],
  );
  const [headerCta, setHeaderCtaState] = useState(defaultHeaderCta);
  const [footerCta, setFooterCtaState] = useState(defaultFooterCta);

  useEffect(() => {
    setHeaderCtaState(defaultHeaderCta);
  }, [defaultHeaderCta]);

  useEffect(() => {
    setFooterCtaState(defaultFooterCta);
  }, [defaultFooterCta]);

  const setHeaderCta = useCallback(
    (override = {}) => {
      setHeaderCtaState({
        label: override.label ?? defaultHeaderCta.label,
        href: override.href ?? defaultHeaderCta.href,
        onClick: override.onClick ?? defaultHeaderCta.onClick,
      });
    },
    [defaultHeaderCta],
  );

  const resetHeaderCta = useCallback(() => {
    setHeaderCtaState(defaultHeaderCta);
  }, [defaultHeaderCta]);

  const setFooterCta = useCallback(
    (override = {}) => {
      setFooterCtaState({
        label: override.label ?? defaultFooterCta.label,
        href: override.href ?? defaultFooterCta.href,
        onClick: override.onClick ?? defaultFooterCta.onClick,
      });
    },
    [defaultFooterCta],
  );

  const resetFooterCta = useCallback(() => {
    setFooterCtaState(defaultFooterCta);
  }, [defaultFooterCta]);

  const contextValue = useMemo(
    () => ({
      headerCta,
      footerCta,
      setHeaderCta,
      resetHeaderCta,
      setFooterCta,
      resetFooterCta,
    }),
    [headerCta, footerCta, setHeaderCta, resetHeaderCta, setFooterCta, resetFooterCta],
  );

  const pilotBanner = (shouldUseApexShell || shellConfig.showHeaderCta) ? (
    <ItineraryPilotBanner
      key={location.pathname}
      placement={shouldUseApexShell ? 'apex_entry' : 'island_entry'}
      href={shouldUseApexShell ? 'https://alakaialoha.com/' : '/interview'}
      ctaLabel={shouldUseApexShell ? 'Choose Your Island' : 'Plan My Trip Free'}
      onOfferViewed={(params) => trackEvent('itinerary_pilot_offer_viewed', params)}
    />
  ) : null;

  return (
    <LayoutCtaContext.Provider value={contextValue}>
      <Helmet>
        <title>{SITE_NAME}</title>
        <meta
          name="description"
          content="Plan Hawaii travel with Alaka'i Aloha through shared planning tools and island-specific content experiences."
        />
      </Helmet>
      {isBareApexHomepage ? (
        <>{pilotBanner}{children}</>
      ) : shouldUseApexShell ? (
        <ApexShell
          currentPath={`${location.pathname}${location.search}${location.hash}`}
          headerCtaLabel={headerCta.label}
          headerCtaHref={headerCta.href}
          headerHomeHref={shellConfig.headerHomeHref}
          headerLogoSrc={shellConfig.headerLogoSrc || getApexHeaderLogoSrc()}
          headerLogoAlt={shellConfig.headerLogoAlt || `${SITE_NAME} logo`}
          footerLogoSrc={shellConfig.footerLogoSrc || getApexFooterLogoSrc()}
          footerLogoAlt={shellConfig.footerLogoAlt || `${SITE_NAME} logo`}
          footerCtaLabel={footerCta.label}
          footerCtaHref={footerCta.href}
          minimalFooter
          backgroundTheme={shellConfig.backgroundTheme}
          onHeaderCtaClick={(event) => {
            if (headerCta.onClick) {
              event.preventDefault();
              headerCta.onClick('header_primary', headerCta.label);
              return;
            }

            trackEvent('apex_navigation_click', {
              placement: 'header_primary',
              label: headerCta.label,
              destination: headerCta.href,
            });
          }}
          onFooterCtaClick={(event) => {
            if (footerCta.onClick) {
              event.preventDefault();
              footerCta.onClick('footer_primary', footerCta.label);
              return;
            }

            trackEvent('apex_navigation_click', {
              placement: 'footer_primary',
              label: footerCta.label,
              destination: footerCta.href,
            });
          }}
        >
          {pilotBanner}
          {children}
        </ApexShell>
      ) : (
        <PublicUiProvider linkComponent={RouterLinkAdapter}>
          <div className={islandShellClassName}>
            <PublicSiteShell
              currentPath={`${location.pathname}${location.search}${location.hash}`}
              navGroups={shellConfig.navGroups}
              headerLinks={shellConfig.headerLinks}
              fullWidth={fullWidth}
              headerBg={headerBg}
              footerBg={footerBg}
              pageClass={pageClass}
              useSiteBackground={useOceanBg}
              headerCtaLabel={headerCta.label}
              headerCtaHref={headerCta.href}
              showHeaderCta={shellConfig.showHeaderCta}
              headerHomeHref={shellConfig.headerHomeHref}
              headerLogoSrc={shellConfig.headerLogoSrc}
              headerLogoAlt={shellConfig.headerLogoAlt}
              onHeaderCtaClick={(event) => {
                if (headerCta.onClick) {
                  event.preventDefault();
                  headerCta.onClick('header_primary', headerCta.label);
                } else {
                  trackEvent('interview_landing_click', 'conversion', 'nav_menu');
                }
              }}
              footerLogoSrc={shellConfig.footerLogoSrc}
              footerLogoHref={shellConfig.footerLogoHref}
              footerLogoAlt={shellConfig.footerLogoAlt}
              footerPrimaryLinks={useMinimalIslandFooter ? [] : shellConfig.footerPrimaryLinks}
              footerColumns={useMinimalIslandFooter ? [] : shellConfig.footerColumns}
              footerMetaLinks={useMinimalIslandFooter ? [] : shellConfig.footerMetaLinks}
              footerLegalLinks={shellConfig.footerLegalLinks}
              footerCopyright={shellConfig.footerCopyright}
              backgroundTheme={shellConfig.backgroundTheme}
            >
              {pilotBanner}
              {children}
            </PublicSiteShell>
          </div>
        </PublicUiProvider>
      )}
    </LayoutCtaContext.Provider>
  );
};

export default Layout;
